import { useState, useEffect, useCallback } from 'react';
import type { Business } from '@/types';

const API_BASE =
  window.location.hostname === 'localhost'
    ? 'http://localhost:8081/api/v1'
    : '/api/v1';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

interface UseBusinessReturn {
  business: Business | null;
  loading: boolean;
  error: string | null;
}

function getCacheKey(slug: string): string {
  return `qrhost_business_${slug}`;
}

function getCached(slug: string): Business | null {
  try {
    const raw = localStorage.getItem(getCacheKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { data: Business; ts: number };
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function setCached(slug: string, data: Business): void {
  try {
    localStorage.setItem(getCacheKey(slug), JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // ignore storage errors
  }
}

export function useBusiness(slug: string): UseBusinessReturn {
  const [business, setBusiness] = useState<Business | null>(() => getCached(slug));
  const [loading, setLoading] = useState<boolean>(!getCached(slug));
  const [error, setError] = useState<string | null>(null);

  const fetchBusiness = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/businesses/${slug}`);
      if (!res.ok) {
        throw new Error(res.status === 404 ? 'Negocio no encontrado' : 'Error al cargar el negocio');
      }
      const data = (await res.json()) as Business;
      setBusiness(data);
      setCached(slug, data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    const cached = getCached(slug);
    if (cached) {
      setBusiness(cached);
      setLoading(false);
      // Refetch en background para actualizar
      fetchBusiness();
    } else {
      fetchBusiness();
    }
  }, [slug, fetchBusiness]);

  return { business, loading, error };
}
